import { Checkbox } from "@chakra-ui/react";
import { FiTrash2, FiMenu } from "react-icons/fi";

import { cn } from "utils/helpers";
import { QuoteTableCell } from "./QuoteTableCell";
import type { QuoteTableCellProps } from "./QuoteTableCell";
import type { QuoteTableColumn, ColumnHeader } from "./quote-table.types";
import { CELL_BORDER, COLUMN_STYLE } from "./quote-table.types";
import type { RfxQuoteItem } from "../../Submission/types/quote.type";
import Tooltip from "components/Tooltip";

export interface QuoteTableRowProps {
	cellProps: Omit<QuoteTableCellProps, "column" | "item">;
	columns: QuoteTableColumn[];
	dragHandleProps?: Record<string, unknown>;
	headers: ColumnHeader[];
	index: number;
	isSelected?: boolean;
	item: RfxQuoteItem;
	showDeleteAction: boolean;
	showDragHandle: boolean;
	showZeroButton: boolean;
	onDeleteClick?: (rowId: number) => void;
	onToggleSelect?: (rowId: number) => void;
	onZeroClick?: (rowId: number) => void;
}

const QuoteTableRow: React.FC<QuoteTableRowProps> = ({
	cellProps,
	columns,
	dragHandleProps,
	headers,
	index,
	isSelected = false,
	item,
	onDeleteClick,
	onToggleSelect,
	onZeroClick,
	showDeleteAction,
	showDragHandle,
	showZeroButton,
}) => {
	return (
		<>
			{showZeroButton && (
				<td
					className={cn(
						CELL_BORDER,
						"text-center bg-gray-50 group-hover:bg-blue-200 group-focus-within:bg-blue-300"
					)}
					style={{ width: "5%" }}
				>
					<Tooltip label="Set all values in this row to zero" shouldWrapChildren>
						<button
							type="button"
							className="rounded border border-gray-300 bg-white px-1 py-0 text-[10px] font-medium leading-4 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
							onClick={() => onZeroClick?.(item.id)}
							data-testid={`zero-row-${item.id}`}
						>
							zero
						</button>
					</Tooltip>
				</td>
			)}
			<td
				className={cn(
					CELL_BORDER,
					"text-center font-roboto text-xs font-normal text-primary-500 bg-gray-50 group-hover:bg-blue-200 group-focus-within:bg-blue-300"
				)}
				style={{ height: "35px" }}
			>
				<div className="flex items-center justify-center gap-1">
					{onToggleSelect && (
						<Checkbox
							size="sm"
							isChecked={isSelected}
							onChange={() => onToggleSelect(item.id)}
							data-testid={`select-row-${item.id}`}
						/>
					)}
					{showDragHandle && (
						<div
							{...dragHandleProps}
							className="cursor-grab text-gray-400"
							data-testid={`row-drag-handle-${item.id}`}
						>
							<FiMenu size={12} />
						</div>
					)}
					<span>{index + 1}</span>
				</div>
			</td>
			{headers
				.filter((h) => h.columnId)
				.map((header) => {
					const col = columns.find((c) => c.id === header.columnId);
					if (!col) {
						return null;
					}
					return (
						<td
							key={header.key}
							className={cn(CELL_BORDER, COLUMN_STYLE)}
						>
							<QuoteTableCell
								{...cellProps}
								column={col}
								item={item}
							/>
						</td>
					);
				})}
			{showDeleteAction && (
				<td
					className={cn(CELL_BORDER, "text-center")}
					style={{ width: "40px" }}
				>
					<button
						type="button"
						className="rounded p-0.5 text-red-400 hover:bg-red-50 hover:text-red-600"
						onClick={() => onDeleteClick?.(item.id)}
						title="Delete row"
						aria-label={`Delete row ${index + 1}`}
						data-testid={`delete-row-${item.id}`}
					>
						<FiTrash2 size={12} />
					</button>
				</td>
			)}
		</>
	);
};

export { QuoteTableRow };
